import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

import { Col, Container, Div, Icon, Input, Row, Text } from '@pinpoint/design-kit';

import CONSTANTS from 'src/store/constants';
import { appActions, agenciesEditActions } from 'src/store/actions';
import { Page } from 'src/containers';
import { ClientsSelection, Message, Button } from 'src/components';

const defaultValues = {
  name: '',
  clients: [],
};

function EditAgency({
  history,
  match: {
    params: { agencyId: agencyIdParam },
  },
  clients,
  fetchClientsStatus,
  agencies,
  fetchAgenciesStatus,
  editAgencyStatus,
  editAgencyError,
  fetchAgencies,
  editAgency,
  enqueueNotification,
}) {
  const { handleSubmit, errors, control, setValue, register: registerInput } = useForm({ defaultValues });

  const onSubmit = (data) => editAgency(agencyIdParam, data);

  const registerInputs = useCallback(() => {
    registerInput(
      { name: 'clients', type: 'custom' },
      {
        validate: (clients_) => (clients_ && clients_.length > 0) || 'Please select atleast one client',
      }
    );
  }, [registerInput]);

  useEffect(() => {
    registerInputs();
    fetchAgencies({ where: { id: agencyIdParam } });
  }, [registerInputs, fetchAgencies, agencyIdParam]);

  const hasData =
    agencies.length > 0 &&
    clients.length > 0 &&
    fetchAgenciesStatus === CONSTANTS.API.SUCCEEDED &&
    fetchClientsStatus === CONSTANTS.API.SUCCEEDED;

  const agency = hasData ? agencies[0] : defaultValues;

  const apiStatuses = Array.of(fetchClientsStatus, fetchAgenciesStatus);

  useEffect(() => {
    if (hasData) {
      setValue('clients', agency.clients);
    }
  }, [fetchAgenciesStatus, fetchClientsStatus, agency.clients, hasData, setValue]);

  useEffect(() => {
    if (editAgencyStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(`Editing the user failed. Reason: ${editAgencyError}`);
    } else if (editAgencyStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('Agency edited successfully', { autoClose: true });
      setTimeout(() => {
        history.push('/agencies');
      }, 1000);
    }
  }, [editAgencyStatus, editAgencyError, enqueueNotification, history]);

  return (
    <Container>
      {hasData ? (
        <Div p={{ y: '2rem' }}>
          <Row>
            <Col>
              <Button
                buttonStyle="primary"
                onClick={() => {
                  history.push('/agencies');
                }}
                data-tip="go back"
              >
                <Icon color="white" name="LongLeft" m={{ r: '0.5rem' }} size="1rem" />
                Back
              </Button>
            </Col>
          </Row>
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <Row p={{ y: '1rem' }}>
              <Col size={{ xs: '12', md: '4' }}>
                <Div d="flex" align="center" minH="2.5rem">
                  <Text>Name</Text>
                </Div>
              </Col>
              <Col size={{ xs: '12', md: '8' }}>
                <Controller
                  name="name"
                  as={Input}
                  control={control}
                  defaultValue={agency.name}
                  rules={{
                    required: 'Agency name is required',
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col offset={{ xs: '0', md: '4' }} size={{ xs: '12', md: '8' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="name" />
                </Text>
              </Col>
            </Row>
            <Row p={{ y: '1rem' }}>
              <Col size={{ xs: '12', md: '4' }}>
                <Div d="flex" align="center" minH="2.5rem">
                  <Text>Client</Text>
                </Div>
              </Col>
              <Col size={{ xs: '12', md: '8' }}>
                <ClientsSelection
                  w="100%"
                  loading={fetchClientsStatus === CONSTANTS.API.STARTED}
                  clients={clients}
                  defaultText=""
                  valueIndices={agency.clients.map((client) => clients.findIndex(({ id }) => id === client.id))}
                  tenant=""
                  multiSelect
                  onSelectClient={(selectedClients) => {
                    setValue('clients', selectedClients, { shouldValidate: true });
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col offset={{ xs: '0', md: '4' }} size={{ xs: '12', md: '8' }}>
                <Text textColor="danger800" textSize="caption">
                  <ErrorMessage errors={errors} name="clients" />
                </Text>
              </Col>
            </Row>
            <Row d="flex" justify="flex-end">
              <Col>
                <Button buttonStyle="primary" m={{ r: '0rem' }} onClick={handleSubmit(onSubmit)}>
                  Save
                  {editAgencyStatus === CONSTANTS.API.STARTED ? (
                    <Icon color="white" name="Loading" m={{ x: '0.5rem' }} />
                  ) : null}
                </Button>
              </Col>
            </Row>
          </form>
        </Div>
      ) : apiStatuses.includes(CONSTANTS.API.STARTED) ? (
        <Message />
      ) : apiStatuses.includes(CONSTANTS.API.FAILED) ? (
        <Message type="error" />
      ) : apiStatuses.every((status) => status === CONSTANTS.API.SUCCEEDED) ? (
        <Message type="info" text="No data" />
      ) : (
        <Message type="info" text="No data" />
      )}
    </Container>
  );
}

const mapStateToProps = ({ app, agenciesEdit }) => ({
  ...app,
  ...agenciesEdit,
});

const mapDispatchToProps = (dispatch) => ({
  fetchAgencies: (...params) => dispatch(agenciesEditActions.fetchAgencies(...params)),
  editAgency: (...params) => dispatch(agenciesEditActions.editAgency(...params)),
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
});

EditAgency.propTypes = {
  clients: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  agencies: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  fetchClientsStatus: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  fetchAgenciesStatus: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  editAgencyStatus: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  editAgencyError: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  editAgency: PropTypes.func.isRequired,
  fetchAgencies: PropTypes.func.isRequired,
  enqueueNotification: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(EditAgency));
